import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  useWizardState,
  useAdvanceWizardStep,
} from "../../hooks/use-wizard.js";
import { useFirstOrg } from "../../hooks/use-orgs.js";
import { AppShell } from "../../components/app-shell.js";
import { WizardShell } from "../../components/wizard-shell.js";
import { Button } from "../../components/button.js";
import type { WizardStepName, WizardStep } from "../../api/types.js";
import { ScanDepthStep } from "./scan-depth.js";
import { ScanFrequencyStep } from "./scan-frequency.js";
import { WcagStandardStep } from "./wcag-standard.js";
import { IntakeChecksStep } from "./intake-checks.js";
import { VerificationStep } from "./verification.js";
import { StatementSetupStep } from "./statement-setup.js";
import { ReviewStep } from "./review.js";
import { PlanSelectionStep } from "./plan-selection.js";

/**
 * Steps that are automatically advanced without user interaction.
 * account_setup and add_website are pre-completed by the time the wizard loads.
 * gsc_integration is not in scope for Block 2b.
 */
const AUTO_ADVANCE_STEPS = new Set<WizardStepName>([
  "account_setup",
  "add_website",
  "gsc_integration",
]);

// Helper to get step order
const STEP_ORDER: WizardStepName[] = [
  "account_setup",
  "plan_selection",
  "add_website",
  "scan_depth",
  "scan_frequency",
  "wcag_standard",
  "discovery_preview",
  "protected_site",
  "statement_setup",
  "review_launch",
];

const STEP_LABELS: Record<WizardStepName, string> = {
  account_setup: "Account setup",
  plan_selection: "Choose plan",
  add_website: "Add website",
  scan_depth: "Scan depth",
  scan_frequency: "Scan frequency",
  wcag_standard: "WCAG standard",
  gsc_integration: "GSC integration",
  discovery_preview: "Intake checks",
  protected_site: "Verification",
  statement_setup: "Statement setup",
  review_launch: "Review",
};

/**
 * Find the previous visible (non-auto-advance) step before the given index.
 */
function findPreviousVisibleStep(fromIdx: number): WizardStepName | null {
  for (let i = fromIdx - 1; i >= 0; i--) {
    const step = STEP_ORDER[i]!;
    if (!AUTO_ADVANCE_STEPS.has(step)) return step;
  }
  return null;
}

/**
 * Find the next visible (non-auto-advance) step after the given index.
 */
function findNextVisibleStep(fromIdx: number): WizardStepName | null {
  for (let i = fromIdx + 1; i < STEP_ORDER.length; i++) {
    const step = STEP_ORDER[i]!;
    if (!AUTO_ADVANCE_STEPS.has(step)) return step;
  }
  return null;
}

const PLAN_LABELS: Record<string, string> = {
  free: "Free — €0/month",
  starter: "Starter — €10/month",
  growth: "Growth — €30/month",
  pro: "Pro — €99/month",
  enterprise: "Enterprise",
};

/**
 * Read-only summary of a completed wizard step.
 * Shown when navigating back to review previous choices.
 */
function StepReviewSummary({
  stepName,
  stepData,
  onForward,
}: {
  stepName: WizardStepName;
  stepData: WizardStep | undefined;
  onForward: () => void;
}) {
  const data = stepData?.data;

  function renderSummary() {
    switch (stepName) {
      case "scan_depth":
        return data?.depth ? <p>Selected: <strong>{String(data.depth)} pages</strong></p> : null;
      case "scan_frequency":
        return data?.frequency ? <p>Selected: <strong>{String(data.frequency)}</strong></p> : null;
      case "wcag_standard":
        return data?.standard ? <p>Selected: <strong>{String(data.standard)}</strong></p> : null;
      case "plan_selection": {
        const planId = String(data?.planId ?? "");
        const planLabel = PLAN_LABELS[planId] ?? planId;
        return data?.planId ? <p>Selected plan: <strong>{planLabel}</strong></p> : null;
      }
      case "statement_setup":
        return (
          <>
            {data?.orgName && <p>Organisation: <strong>{String(data.orgName)}</strong></p>}
            {data?.accessibilityContactEmail && <p>Contact: <strong>{String(data.accessibilityContactEmail)}</strong></p>}
            {data?.orgType && <p>Type: <strong>{String(data.orgType)}</strong></p>}
          </>
        );
      case "protected_site":
        return <p>{data?.verified ? <strong>Verified</strong> : <strong>Skipped</strong>}</p>;
      case "discovery_preview": {
        const warnings = Number(data?.warningCount ?? 0);
        const advisories = Number(data?.advisoryCount ?? 0);
        return (
          <>
            {warnings > 0 && <p>Warnings: <strong>{warnings}</strong></p>}
            {advisories > 0 && <p>Advisories: <strong>{advisories}</strong></p>}
            {warnings === 0 && advisories === 0 && <p>No issues found</p>}
          </>
        );
      }
      default:
        return stepData?.outcome
          ? <p>Completed with outcome: <strong>{stepData.outcome}</strong></p>
          : <p>Completed</p>;
    }
  }

  return (
    <div className="wizard-content">
      <h2 className="wizard-content__title">{STEP_LABELS[stepName]}</h2>
      <p className="wizard-content__subtitle">
        You completed this step earlier. Here is what you selected:
      </p>
      <div className="step-review-summary">
        {renderSummary()}
      </div>
      <div className="wizard-content__actions">
        <Button onClick={onForward}>Continue forward</Button>
      </div>
    </div>
  );
}

export function WizardRouter() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const org = useFirstOrg();

  const { data: wizardState, isLoading, error } = useWizardState(websiteId);
  const advance = useAdvanceWizardStep(websiteId ?? "");

  // Local "viewing" step — when set, we display this step instead of currentStep.
  // No backend call is made; this is purely a UI navigation override.
  const [viewStep, setViewStep] = useState<WizardStepName | null>(null);

  // Clear viewStep when backend state changes (e.g. after a successful submit)
  useEffect(() => {
    setViewStep(null);
  }, [wizardState?.currentStep]);

  // Auto-advance steps that require no user input
  useEffect(() => {
    if (!wizardState || advance.isPending || advance.isError || viewStep) return;
    if (AUTO_ADVANCE_STEPS.has(wizardState.currentStep)) {
      advance.mutate({ step: wizardState.currentStep, outcome: "pass" });
    }
  }, [wizardState, advance, viewStep]);

  // Handle back button — purely local navigation, no API call
  const handleBack = useCallback(() => {
    if (!wizardState) return;
    const displayedStep = viewStep ?? wizardState.currentStep;
    const displayedIdx = STEP_ORDER.indexOf(displayedStep);
    const prev = findPreviousVisibleStep(displayedIdx);
    if (prev) setViewStep(prev);
  }, [wizardState, viewStep]);

  // Handle forward from a review step — advance view toward currentStep
  const handleForwardFromReview = useCallback(() => {
    if (!wizardState || !viewStep) return;
    const viewIdx = STEP_ORDER.indexOf(viewStep);
    const next = findNextVisibleStep(viewIdx);
    if (!next || STEP_ORDER.indexOf(next) >= STEP_ORDER.indexOf(wizardState.currentStep)) {
      setViewStep(null);
    } else {
      setViewStep(next);
    }
  }, [wizardState, viewStep]);

  const displayedStep = viewStep ?? wizardState?.currentStep;
  const isReviewingPastStep =
    viewStep !== null &&
    wizardState !== undefined &&
    viewStep !== wizardState.currentStep;

  // Determine if we can go back
  const canGoBack =
    wizardState &&
    displayedStep &&
    findPreviousVisibleStep(STEP_ORDER.indexOf(displayedStep)) !== null &&
    !advance.isPending;

  if (isLoading) {
    return (
      <AppShell>
        <div className="loading-text">Loading wizard…</div>
      </AppShell>
    );
  }

  if (error || !wizardState) {
    return (
      <AppShell>
        <div className="alert alert--error">
          Failed to load wizard state. Please refresh the page.
        </div>
      </AppShell>
    );
  }

  const completedSteps = wizardState.steps
    .filter((s) => s.completedAt != null)
    .map((s) => s.step);

  const currentStep = displayedStep ?? wizardState.currentStep;

  function renderStep() {
    if (!wizardState || !websiteId) return null;

    // When reviewing a past step, show read-only summary
    if (isReviewingPastStep && viewStep) {
      const stepData = wizardState.steps.find((s) => s.step === viewStep);
      return (
        <StepReviewSummary
          stepName={viewStep}
          stepData={stepData}
          onForward={handleForwardFromReview}
        />
      );
    }

    // Show a spinner while auto-advancing
    if (AUTO_ADVANCE_STEPS.has(currentStep)) {
      return <div className="loading-text">Setting up…</div>;
    }

    switch (currentStep) {
      case "scan_depth":
        return (
          <ScanDepthStep websiteId={websiteId} wizardState={wizardState} />
        );
      case "scan_frequency":
        return (
          <ScanFrequencyStep websiteId={websiteId} wizardState={wizardState} />
        );
      case "wcag_standard":
        return (
          <WcagStandardStep websiteId={websiteId} wizardState={wizardState} />
        );
      case "discovery_preview":
        return (
          <IntakeChecksStep websiteId={websiteId} wizardState={wizardState} />
        );
      case "protected_site":
        return (
          <VerificationStep websiteId={websiteId} wizardState={wizardState} />
        );
      case "statement_setup":
        return (
          <StatementSetupStep
            websiteId={websiteId}
            wizardState={wizardState}
            org={org}
          />
        );
      case "review_launch":
        return <ReviewStep websiteId={websiteId} wizardState={wizardState} />;
      case "plan_selection":
        return (
          <PlanSelectionStep
            websiteId={websiteId}
            wizardState={wizardState}
            orgId={wizardState.orgId}
          />
        );
      default:
        return (
          <div className="wizard-content">
            <p className="text-muted">Step not yet implemented.</p>
          </div>
        );
    }
  }

  return (
    <AppShell>
      <WizardShell
        currentStep={currentStep}
        completedSteps={completedSteps}
        onBack={handleBack}
        canGoBack={canGoBack}
      >
        {renderStep()}
      </WizardShell>
    </AppShell>
  );
}
